/**
 * API Service for Admin Dashboard
 * Handles authenticated API requests with automatic token management
 */

import logger from '../utils/logger';
import { markHandled } from '../monitoring/sentry';

// Avoid mixed content: when page is HTTPS, never use an HTTP API URL (use same-origin relative paths).
export function getApiBaseUrl() {
  const env = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && env.startsWith('http://')) return '';
  return env;
}

const API_BASE_URL = getApiBaseUrl();

/**
 * Check if token is old Base64 format (should be JWT)
 */
const isOldBase64Token = (token) => {
  if (!token) return false;
  // JWT tokens have 3 parts separated by dots: header.payload.signature
  // Base64 tokens don't have this structure
  const parts = token.split('.');
  return parts.length !== 3;
};

/**
 * Clear all authentication data
 * Uses sessionStorage for tokens (more secure than localStorage)
 */
const clearAuthData = () => {
  // Clear tokens from sessionStorage (more secure)
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('adminToken');
  sessionStorage.removeItem('refreshToken');
  // Keep user data in localStorage (less sensitive, persists across tabs)
  localStorage.removeItem('user');
  localStorage.removeItem('adminUser');
};

export const SESSION_SUPERSEDED_MESSAGE =
  'Your session has expired because your account was signed in on another device. Please log in again.';
const SESSION_SUPERSEDED_FLASH_KEY = 'syllera_session_superseded_message';

function clearStudentSessionStorage() {
  try {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  } catch (_) {
    /* ignore quota / private-mode errors */
  }
}

/** True once this page has learned the server rejected the current session. */
export function isStudentSessionRejected() {
  if (typeof window === 'undefined') return false;
  if (window.__sylleraHandlingSessionSuperseded) return true;
  try {
    return Boolean(sessionStorage.getItem(SESSION_SUPERSEDED_FLASH_KEY));
  } catch (_) {
    return false;
  }
}

export function consumeSessionSupersededFlash() {
  try {
    const msg = sessionStorage.getItem(SESSION_SUPERSEDED_FLASH_KEY);
    if (msg) {
      sessionStorage.removeItem(SESSION_SUPERSEDED_FLASH_KEY);
      return msg;
    }
  } catch (_) {
    /* ignore */
  }
  return '';
}

export function handleSessionSuperseded(message) {
  if (typeof window === 'undefined') return;
  if (window.__sylleraHandlingSessionSuperseded) return;
  window.__sylleraHandlingSessionSuperseded = true;

  const msg = (message && String(message).trim()) || SESSION_SUPERSEDED_MESSAGE;
  try {
    sessionStorage.setItem(SESSION_SUPERSEDED_FLASH_KEY, msg);
  } catch (_) {
    /* ignore */
  }
  clearStudentSessionStorage();

  const path = window.location.pathname || '/';
  const onLanding = path === '/' || path === '';
  const alreadyFlagged = (() => {
    try {
      return new URLSearchParams(window.location.search).get('session') === 'superseded';
    } catch (_) {
      return false;
    }
  })();
  if (onLanding && alreadyFlagged) {
    return;
  }
  window.location.replace('/?session=superseded#login');
}

async function maybeHandleSessionSuperseded(response) {
  if (!response || response.status !== 401) return false;
  try {
    const data = await response.clone().json();
    if (data && data.code === 'SESSION_SUPERSEDED') {
      handleSessionSuperseded(data.message);
      return true;
    }
  } catch (_) {
    /* non-JSON 401s keep existing behavior */
  }
  return false;
}

/**
 * Student dashboard fetch wrapper. Detects SESSION_SUPERSEDED without treating every 401 as that code.
 */
export async function studentAuthFetch(input, init) {
  const response = await fetch(input, init);
  await maybeHandleSessionSuperseded(response);
  return response;
}

export function installStudentSessionFetchGuard() {
  if (typeof window === 'undefined' || window.__sylleraSessionFetchGuard) return;
  window.__sylleraSessionFetchGuard = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async function sylleraGuardedFetch(...args) {
    const response = await originalFetch(...args);
    await maybeHandleSessionSuperseded(response);
    return response;
  };
}

/**
 * Get authentication token from sessionStorage (more secure than localStorage)
 * Falls back to localStorage for backward compatibility during migration
 */
export const getAuthToken = () => {
  // Try sessionStorage first (more secure)
  let token = sessionStorage.getItem('token') || sessionStorage.getItem('adminToken');
  
  // Fallback to localStorage for backward compatibility (remove after migration)
  if (!token) {
    token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    // Migrate to sessionStorage if found in localStorage
    if (token) {
      sessionStorage.setItem('token', token);
      localStorage.removeItem('token');
      localStorage.removeItem('adminToken');
    }
  }
  
  // If token exists but is old Base64 format, clear it
  if (token && isOldBase64Token(token)) {
    logger.warn('⚠️ Old Base64 token detected. Clearing authentication data. Please login again.');
    clearAuthData();
    // Redirect to login if we're not already there
    if (window.location.pathname !== '/' && !window.location.pathname.includes('login')) {
      window.location.href = '/';
    }
    return null;
  }
  
  return token;
};

/**
 * Get refresh token from sessionStorage (more secure than localStorage)
 * Falls back to localStorage for backward compatibility
 */
const getRefreshToken = () => {
  // Try sessionStorage first
  let refreshToken = sessionStorage.getItem('refreshToken');
  
  // Fallback to localStorage for backward compatibility
  if (!refreshToken) {
    refreshToken = localStorage.getItem('refreshToken');
    // Migrate to sessionStorage if found
    if (refreshToken) {
      sessionStorage.setItem('refreshToken', refreshToken);
      localStorage.removeItem('refreshToken');
    }
  }
  
  return refreshToken;
};

/**
 * Refresh access token using refresh token
 */
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const fatal =
        data.code === 'SESSION_IDLE' ||
        data.code === 'ABSOLUTE_EXPIRED' ||
        data.code === 'SESSION_UNKNOWN' ||
        data.code === 'REFRESH_EXPIRED' ||
        data.code === 'REFRESH_INVALID';
      if (fatal) {
        clearAuthData();
        if (window.location.pathname !== '/' && !window.location.pathname.includes('login')) {
          window.location.href = '/';
        }
      }
      throw new Error(data.message || 'Failed to refresh token');
    }

    if (data.success && data.token) {
      sessionStorage.setItem('token', data.token);
      localStorage.removeItem('token');
      localStorage.removeItem('adminToken');
      if (data.refreshToken) {
        sessionStorage.setItem('refreshToken', data.refreshToken);
        localStorage.removeItem('refreshToken');
      }
      return data.token;
    }

    throw new Error('Invalid refresh response');
  } catch (error) {
    clearAuthData();
    throw error;
  }
};

// Track if we're currently refreshing to avoid infinite loops
let isRefreshing = false;
let refreshPromise = null;

// HTTP status errors are expected (auth, validation) or already reported by the fetch monitor (5xx),
// so logger.error must not report them again.
const httpError = (message) => markHandled(new Error(message));

/**
 * Handle API response errors with automatic token refresh
 */
const handleResponse = async (response, originalRequest) => {
  // Handle 401 (Unauthorized) - try to refresh token
  if (response.status === 401) {
    const errorData = await response.json().catch(() => ({}));

    if (errorData.code === 'SESSION_SUPERSEDED') {
      handleSessionSuperseded(errorData.message);
      throw httpError(errorData.message || SESSION_SUPERSEDED_MESSAGE);
    }
    
    // Check if it's an invalid token (old Base64 token or malformed JWT)
    if (errorData.message && errorData.message.includes('Invalid token')) {
      logger.warn('⚠️ Invalid token detected. Clearing authentication data.');
      clearAuthData();
      if (window.location.pathname !== '/' && !window.location.pathname.includes('login')) {
        window.location.href = '/';
      }
      throw httpError('Invalid token. Please login again.');
    }
    
    // Check if it's a token expiration error
    if (errorData.message && (errorData.message.includes('expired') || errorData.message.includes('Token'))) {
      // Prevent multiple simultaneous refresh attempts
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshAccessToken();
      }
      
      try {
        // Wait for token refresh
        const newToken = await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;
        
        // Retry the original request with new token
        if (originalRequest) {
          const retryConfig = {
            ...originalRequest,
            headers: {
              ...originalRequest.headers,
              'Authorization': `Bearer ${newToken}`,
            },
          };
          const retryResponse = await fetch(originalRequest.url, retryConfig);
          return await handleResponse(retryResponse, null);
        }
      } catch (refreshError) {
        isRefreshing = false;
        refreshPromise = null;
        // Refresh failed, redirect to login
        clearAuthData();
        if (window.location.pathname !== '/' && !window.location.pathname.includes('login')) {
          window.location.href = '/';
        }
        throw httpError('Session expired. Please login again.');
      }
    }
    
    // Other 401 errors (invalid credentials, etc.)
    throw httpError(errorData.message || 'Unauthorized. Please check your credentials.');
  }

  // Handle 403 (Forbidden) - token expired or invalid
  if (response.status === 403) {
    clearAuthData();
    if (window.location.pathname !== '/' && !window.location.pathname.includes('login')) {
      window.location.href = '/';
    }
    throw httpError('Access forbidden. Please login again.');
  }

  // Handle other errors
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw httpError(errorData.message || `API Error: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

/**
 * Make authenticated API request with automatic token refresh
 */
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers,
  };

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, config);
    return await handleResponse(response, { ...config, url });
  } catch (error) {
    logger.error('API Request Error:', error);
    throw error;
  }
};

/**
 * API Service Methods
 */
export const api = {
  // GET request
  get: (endpoint, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'GET',
    });
  },

  // POST request
  post: (endpoint, data, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // PUT request
  put: (endpoint, data, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // PATCH request
  patch: (endpoint, data, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // DELETE request
  delete: (endpoint, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'DELETE',
    });
  },
};

/**
 * User Management API
 */
export const usersAPI = {
  // Get all users with pagination
  getUsers: (params = {}) => {
    const { page = 1, limit = 25, search = '', role = '', status = '' } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
      ...(role && { role }),
      ...(status && { status }),
    });
    return api.get(`/api/admin/users?${queryParams}`);
  },

  // Get single user
  getUser: (id) => {
    return api.get(`/api/admin/users/${id}`);
  },

  // Create user
  createUser: (userData) => {
    return api.post('/api/admin/users', userData);
  },

  // Update user
  updateUser: (id, userData) => {
    return api.put(`/api/admin/users/${id}`, userData);
  },

  // Delete user
  deleteUser: (id) => {
    return api.delete(`/api/admin/users/${id}`);
  },

  // Bulk operations
  bulkDelete: (ids) => {
    return api.post('/api/admin/users/bulk-delete', { ids });
  },

  bulkActivate: (ids) => {
    return api.post('/api/admin/users/bulk-activate', { ids });
  },

  bulkDeactivate: (ids) => {
    return api.post('/api/admin/users/bulk-deactivate', { ids });
  },
};

/**
 * Dashboard API
 */
export const dashboardAPI = {
  // Get dashboard stats
  getStats: () => {
    return api.get('/dashboard/stats');
  },

  // Get analytics
  getAnalytics: (params = {}) => {
    const queryParams = new URLSearchParams(params);
    const qs = queryParams.toString();
    return api.get(qs ? `/dashboard/analytics?${qs}` : '/dashboard/analytics');
  },

  getActiveUsers: (limit = 200) => {
    return api.get(`/dashboard/active-users?limit=${limit}`);
  },

  getActiveUsersToday: (limit = 500) => {
    return api.get(`/dashboard/active-users-today?limit=${limit}`);
  },

  getActiveUsersWindow: ({ days = 7, limit = 2000 } = {}) => {
    return api.get(`/dashboard/active-users-window?days=${days}&limit=${limit}`);
  },

  getActivityChart: () => {
    return api.get('/dashboard/activity-chart');
  },

  // Get statistics
  getStatistics: () => {
    return api.get('/dashboard/statistics');
  },

  // Get activity
  getActivity: () => {
    return api.get('/dashboard/activity');
  },

  // Get monitoring data
  getMonitoring: () => {
    return api.get('/dashboard/monitoring');
  },

  // Get reports
  getReports: () => {
    return api.get('/dashboard/reports');
  },

  // Generate report
  generateReport: (reportData) => {
    return api.post('/dashboard/generate-report', reportData);
  },
};

/**
 * Payments API
 */
export const paymentsAPI = {
  getOverview: () => {
    return api.get('/api/dashboard-payments/overview');
  },
};

/**
 * Audit Logs API
 */
export const auditLogsAPI = {
  getLogs: (params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`/api/audit-logs?${queryParams}`);
  },
  getStats: () => {
    return api.get('/api/audit-logs/stats');
  },
  getLog: (id) => {
    return api.get(`/api/audit-logs/${id}`);
  },
  exportLogs: (filters) => {
    return api.post('/api/audit-logs/export', filters);
  },
};

/**
 * User Profile API
 */
export const userProfileAPI = {
  // Get user profile
  getProfile: () => {
    return api.get('/api/user/profile');
  },

  // Update user language preference
  updateLanguage: (userId, language) => {
    return api.patch(`/api/user/${userId}/language`, { language });
  },
};

// Convenience function for updating user language
export const updateUserLanguage = async (userId, language) => {
  try {
    const response = await userProfileAPI.updateLanguage(userId, language);
    return response;
  } catch (error) {
    logger.error('Error updating user language:', error);
    throw error;
  }
};

export default api;

